import React, { useEffect, useRef, useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import DOMPurify from 'dompurify';
import CollapsableContent from 'Components/Common/CollapsableContent';
import { sanitizeURLs } from 'utils/helpers';

const listToHTML = (items: string[]): string => {
    return `<ul>${items.map(item => `<li>${item}</li>`).join('')}</ul>`;
};

const parseHTMLToList = (html: string): string[] => {
    const container = document.createElement('div');
    container.innerHTML = html;

    const items: string[] = [];
    container.querySelectorAll('ul li').forEach((li) => {
        const text = li.textContent?.trim();
        if (text) items.push(text);
    });

    return items;
};

interface ListEditorProps {
    value: string[];
    onChange: (items: string[]) => void;
    title?: string;
    disabled?: boolean
}

const ListEditor: React.FC<ListEditorProps> = ({ value, onChange, title, disabled=false }) => {
    const [html, setHtml] = useState('');
    const [editMode, setEditMode] = useState(false);
    const quillRef = useRef<ReactQuill | null>(null);

    useEffect(() => {
        setHtml(listToHTML(value));
    }, [value]);

    useEffect(() => {
        const quill = quillRef.current?.getEditor();
        if (!quill) return;

        const html = quill.root.innerHTML;
        const contents = quill.getContents();

        const isEmpty = contents.ops?.length === 1 && contents.ops[0].insert === '\n';
        const isParagraph = html === '<p><br></p>';

        if (isEmpty || isParagraph) {
            quill.formatLine(0, 1, { list: 'bullet' });
        }
    }, [editMode, html]);

    const handleChange = (content: string) => {
        setHtml(content);
        const items = parseHTMLToList(content);
        if (JSON.stringify(items) !== JSON.stringify(value)) {
            onChange(items);
        }
    };

    const modules = {
        toolbar: [['list']],
    };

    const formats = ['list'];

    return (
        <div className="script-card">
            <div className="script-card-header">
                <span className="script-name">{title}</span>
                {
                    !disabled && 
                    <div className="script-actions">
                        {editMode ? (
                            <button className="edit-script" onClick={() => setEditMode(false)}>
                                <span>Preview</span>
                            </button>
                        ) : (
                            <button className="edit-script" onClick={() => setEditMode(true)}>
                                <span>Edit</span>
                            </button>
                        )}
                    </div>
                }
            </div>

            <div className={`script-card-body list-editor ${editMode ? 'p-0' : ''}`}>
                {editMode ? (
                    <ReactQuill
                        ref={quillRef}
                        theme="snow"
                        value={html}
                        onChange={handleChange}
                        modules={modules}
                        formats={formats}
                        readOnly={disabled}
                        placeholder="📝 Start writing bullet points..."
                    />
                ) : (
                    <CollapsableContent noPaddingReadMoreButton>
                        <div className="content-wrapper">
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: DOMPurify.sanitize(sanitizeURLs(html)),
                                }}
                            />
                        </div>
                    </CollapsableContent>
                )}
            </div>
        </div>
    );
};

export default ListEditor;